import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  Menu,
  MenuItem,
  IconButton,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
  ListItemButton,
  Fade,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import LanguageIcon from '@mui/icons-material/Language';
import { motion, AnimatePresence } from 'framer-motion';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
}));

const Logo = styled('img')(({ theme }) => ({
  height: '65px',
  marginRight: theme.spacing(2),
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const NavButton = styled(Button)(({ theme, active }) => ({
  color: 'white',
  fontWeight: active ? 600 : 500,
  fontSize: '0.85rem',
  position: 'relative',
  padding: theme.spacing(1, 2),
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: '5px',
    left: '50%',
    width: active ? '80%' : '0%',
    height: '2px',
    backgroundColor: 'white',
    transition: 'all 0.3s ease',
    transform: 'translateX(-50%)',
  },
  '&:hover:after': {
    width: '80%',
  },
}));

const LanguageButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  borderRadius: '20px',
  padding: theme.spacing(0.5, 2),
  backgroundColor: 'white',
  color: theme.palette.primary.main,
  border: '2px solid white',
  fontWeight: 600,
  fontSize: '0.8rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'white',
    transform: 'scale(1.05)',
  },
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
  },
}));

const menuItems = {
  about: {
    en: [
      { title: 'Vision and Aim', path: '/vision-aim' },
      { title: 'Background History', path: '/history' },
      { title: 'Branch Monasteries', path: '/branch-monasteries' },
    ],
    mm: [
      { title: 'ရည်မှန်းချက်နှင့် ရည်ရွယ်ချက်', path: '/vision-aim' },
      { title: 'နောက်ခံသမိုင်း', path: '/history' },
      { title: 'ဆက်စပ်ကျောင်းတိုက်များ', path: '/branch-monasteries' },
    ]
  },
  regulations: {
    en: [
      { title: 'Rules', path: '/rules' },
      { title: 'Daily Routine', path: '/daily-routine' },
      { title: 'Qualities of Student', path: '/student-qualities' },
    ],
    mm: [
      { title: 'စည်းကမ်းများ', path: '/rules' },
      { title: 'နေ့စဉ်လုပ်ငန်းစဉ်', path: '/daily-routine' },
      { title: 'စာသင်သား အရည်အချင်း များ', path: '/student-qualities' },
    ]
  },
  administratives: {
    en: [
      { title: 'Hierarchy', path: '/hierarchy' },
      { title: 'Education Board', path: '/education-board' },
      { title: 'Management Board', path: '/management-board' },
    ],
    mm: [
      { title: 'စီမံအုပ်ချုပ်မှုအဆင့်ဆင့်', path: '/hierarchy' },
      { title: 'ပညာရေးဘုတ်အဖွဲ့', path: '/education-board' },
      { title: 'စီမံခန့်ခွဲရေးဘုတ်အဖွဲ့', path: '/management-board' },
    ]
  },
  academic: {
    en: [
      { title: 'Courses', path: '/courses' },
      { title: 'Teaching Methods', path: '/teaching-methods' },
    ],
    mm: [
      { title: 'သင်တန်းများ', path: '/courses' },
      { title: 'သင်ကြားရေးနည်းစနစ်', path: '/teaching-methods' },
    ]
  }
};

// Add new styled components for dropdown
const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    marginTop: '8px',
    minWidth: '200px',
    borderRadius: '8px',
    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  fontSize: '0.8rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
    transform: 'translateX(5px)',
  },
}));

// Mobile menu components
const DrawerHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const MobileNavItem = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  '&:hover': {
    backgroundColor: 'rgba(77, 0, 0, 0.1)',
  },
}));

const MobileNav = ({ language, toggleLanguage }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState('');
  const location = useLocation();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleSubMenuClick = (menuName) => {
    setOpenSubMenu(openSubMenu === menuName ? '' : menuName);
  };

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="menu"
        onClick={handleDrawerToggle}
        sx={{ ml: 'auto' }}
      >
        <MenuIcon />
      </IconButton>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            width: '280px',
          },
        }}
      >
        <DrawerHeader>
          <LanguageButton onClick={toggleLanguage}>
            <LanguageIcon />
            {language === 'mm' ? 'မြန်မာ' : 'ENG'}
          </LanguageButton>
        </DrawerHeader>

        <List>
          <MobileNavItem
            component={Link}
            to="/"
            onClick={handleDrawerToggle}
            selected={location.pathname === '/'}
          >
            <ListItemText primary={language === 'en' ? 'Home' : 'ပင်မစာမျက်နှာ'} />
          </MobileNavItem>

          {Object.entries(menuItems).map(([section, items]) => (
            <React.Fragment key={section}>
              <MobileNavItem onClick={() => handleSubMenuClick(section)}>
                <ListItemText 
                  primary={
                    language === 'en' 
                      ? section.charAt(0).toUpperCase() + section.slice(1) 
                      : section === 'about' ? 'ကျောင်းတိုက်အကြောင်း'
                      : section === 'regulations' ? 'စည်းကမ်းများ'
                      : section === 'administratives' ? 'စီမံအုပ်ချုပ်ရေး'
                      : 'ပညာရေး'
                  }
                />
                {openSubMenu === section ? <ExpandLess /> : <ExpandMore />}
              </MobileNavItem>
              <Collapse in={openSubMenu === section} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {items[language].map((item) => (
                    <MobileNavItem
                      key={item.path}
                      component={Link}
                      to={item.path}
                      onClick={handleDrawerToggle}
                      sx={{ pl: 4 }}
                      selected={location.pathname === item.path}
                    >
                      <ListItemText primary={item.title} />
                    </MobileNavItem>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}

          {/* Static menu items */}
          {['Events & News', 'Publication', 'Foundation'].map((item) => (
            <MobileNavItem
              key={item}
              component={Link}
              to={`/${item.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={handleDrawerToggle}
              selected={location.pathname === `/${item.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <ListItemText 
                primary={
                  language === 'en' 
                    ? item 
                    : item === 'Events & News' ? 'သတင်းနှင့်လှုပ်ရှားမှုများ'
                    : item === 'Publication' ? 'ထုတ်ဝေမှုများ'
                    : 'ဖောင်ဒေးရှင်း'
                }
              />
            </MobileNavItem>
          ))}
        </List>
      </Drawer>
    </>
  );
};

// Update the DesktopNav component to include the language button:
const DesktopNav = ({ location, language, toggleLanguage }) => {
  const [anchorEls, setAnchorEls] = useState({});

  const handleMenuOpen = (event, section) => {
    setAnchorEls({ ...anchorEls, [section]: event.currentTarget });
  };

  const handleMenuClose = (section) => {
    setAnchorEls({ ...anchorEls, [section]: null });
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1 }}>
        {/* Home button */}
        <NavButton
          component={Link}
          to="/"
          active={location.pathname === '/'}
        >
          {language === 'en' ? 'Home' : 'ပင်မစာမျက်နှာ'}
        </NavButton>

        {/* Dropdown Menus */}
        {[
          { key: 'about', en: 'About', mm: 'ကျောင်းတိုက်အကြောင်း' },
          { key: 'regulations', en: 'Regulations', mm: 'စည်းကမ်းများ' },
          { key: 'administratives', en: 'Administratives', mm: 'စီမံအုပ်ချုပ်ရေး' },
          { key: 'academic', en: 'Academic', mm: 'ပညာရေး' },
        ].map((section) => (
          <React.Fragment key={section.key}>
            <NavButton
              onClick={(e) => handleMenuOpen(e, section.key)}
              endIcon={<ExpandMore />}
              active={menuItems[section.key][language].some(item => location.pathname === item.path)}
            >
              {language === 'en' ? section.en : section.mm}
            </NavButton>
            <StyledMenu
              anchorEl={anchorEls[section.key]}
              open={Boolean(anchorEls[section.key])}
              onClose={() => handleMenuClose(section.key)}
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 200 }}
            >
              {menuItems[section.key][language].map((item) => (
                <StyledMenuItem
                  key={item.path}
                  component={Link}
                  to={item.path}
                  onClick={() => handleMenuClose(section.key)}
                >
                  {item.title}
                </StyledMenuItem>
              ))}
            </StyledMenu>
          </React.Fragment>
        ))}

        {/* Static menu items */}
        {[
          { path: '/events', en: 'Events & News', mm: 'သတင်းနှင့်လှုပ်ရှားမှုများ' },
          { path: '/publication', en: 'Publication', mm: 'ထုတ်ဝေမှုများ' },
          { path: '/foundation', en: 'Foundation', mm: 'ဖောင်ဒေးရှင်း' },
        ].map((item) => (
          <NavButton
            key={item.path}
            component={Link}
            to={item.path}
            active={location.pathname === item.path}
          >
            {language === 'en' ? item.en : item.mm}
          </NavButton>
        ))}
      </Box>

      {/* Language Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
        <LanguageButton onClick={toggleLanguage}>
          <LanguageIcon sx={{ mr: 1 }} />
          {language === 'mm' ? 'မြန်မာ' : 'ENG'}
        </LanguageButton>
      </Box>
    </>
  );
};

const Navbar = ({ language, toggleLanguage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  return (
    <StyledAppBar position="sticky">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Link to="/">
            <Logo 
              src={process.env.PUBLIC_URL + '/assets/img/logo.png'} 
              alt="Mahavihara Forest Monastery"
            />
          </Link>

          {isMobile ? (
            <MobileNav
              language={language}
              toggleLanguage={toggleLanguage}
            />
          ) : (
            <DesktopNav
              location={location}
              language={language}
              toggleLanguage={toggleLanguage}
            />
          )}
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
};

export default Navbar; 